import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Route, MapPin, Users, Plus, Calendar, Clock, ThumbsUp, ThumbsDown,
  Share2, Trash2, ChevronRight, ArrowLeft, Navigation
} from 'lucide-react';
import { toast } from 'sonner';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Custom marker for crawl stops
const stopIcon = (order) => new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="
    background: linear-gradient(135deg, #9333EA, #EC4899);
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: bold;
    font-size: 14px;
    border: 3px solid white;
    box-shadow: 0 2px 10px rgba(147, 51, 234, 0.5);
  ">${order}</div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16]
});

const BarCrawl = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [crawls, setCrawls] = useState([]);
  const [bars, setBars] = useState([]);
  const [selectedCrawl, setSelectedCrawl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  
  // Create form state
  const [newCrawl, setNewCrawl] = useState({
    name: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    start_time: '20:00',
    bar_ids: [],
    is_public: false
  });

  useEffect(() => {
    fetchCrawls();
    fetchBars();
  }, []);

  const fetchCrawls = async () => {
    try {
      const response = await axios.get(`${API}/bar-crawl/list`);
      setCrawls(response.data || []);
    } catch (error) {
      console.log('Bar crawls not available');
    } finally {
      setLoading(false);
    }
  };

  const fetchBars = async () => {
    try {
      const response = await axios.get(`${API}/bars`);
      setBars(response.data || []);
    } catch (error) {
      console.log('Bars not available');
    }
  };

  const handleCreateCrawl = async () => {
    if (!newCrawl.name || newCrawl.bar_ids.length < 2) {
      toast.error('Please provide a name and select at least 2 bars');
      return;
    }

    try {
      const response = await axios.post(`${API}/bar-crawl/create`, newCrawl);
      setCrawls([response.data, ...crawls]);
      setCreateDialogOpen(false);
      setNewCrawl({
        name: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        start_time: '20:00',
        bar_ids: [],
        is_public: false
      });
      toast.success('Bar crawl created!');
    } catch (error) {
      toast.error('Failed to create bar crawl');
    }
  };

  const handleJoinCrawl = async (crawlId) => {
    try {
      await axios.post(`${API}/bar-crawl/${crawlId}/join`);
      fetchCrawls();
      toast.success('Joined the bar crawl!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join');
    }
  };

  const handleVote = async (crawlId, barId, vote) => {
    try {
      await axios.post(`${API}/bar-crawl/vote`, {
        crawl_id: crawlId,
        bar_id: barId,
        vote: vote
      });
      // Refresh the selected crawl
      if (selectedCrawl) {
        const response = await axios.get(`${API}/bar-crawl/${crawlId}`);
        setSelectedCrawl(response.data);
      }
    } catch (error) {
      toast.error('Failed to vote');
    }
  };

  const handleDeleteCrawl = async (crawlId) => {
    try {
      await axios.delete(`${API}/bar-crawl/${crawlId}`);
      setCrawls(crawls.filter(c => c.id !== crawlId));
      setSelectedCrawl(null);
      toast.success('Bar crawl deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const toggleBarSelection = (barId) => {
    setNewCrawl(prev => ({
      ...prev,
      bar_ids: prev.bar_ids.includes(barId)
        ? prev.bar_ids.filter(id => id !== barId)
        : [...prev.bar_ids, barId]
    }));
  };

  const isParticipant = (crawl) => {
    return crawl.participants?.some(p => p.user_id === user?.id);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white">Loading bar crawls...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'transparent' }}>
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-slate-700">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/home')}
              className="text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <Route className="w-6 h-6 text-purple-400" />
              <h1 className="text-xl font-bold text-white">Bar Crawl Planner</h1>
            </div>
          </div>
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                <Plus className="w-4 h-4 mr-2" />
                Create Crawl
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Plan Your Bar Crawl</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Crawl Name</label>
                  <Input
                    placeholder="Saturday Night Crawl"
                    value={newCrawl.name}
                    onChange={(e) => setNewCrawl({ ...newCrawl, name: e.target.value })}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-400 mb-1 block">Date</label>
                    <Input
                      type="date"
                      value={newCrawl.date}
                      onChange={(e) => setNewCrawl({ ...newCrawl, date: e.target.value })}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400 mb-1 block">Start Time</label>
                    <Input
                      type="time"
                      value={newCrawl.start_time}
                      onChange={(e) => setNewCrawl({ ...newCrawl, start_time: e.target.value })}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Description (optional)</label>
                  <Input
                    placeholder="Let's hit the best spots downtown!"
                    value={newCrawl.description}
                    onChange={(e) => setNewCrawl({ ...newCrawl, description: e.target.value })}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>

                <div>
                  <label className="text-sm text-slate-400 mb-2 block">
                    Select Bars (in order) - {newCrawl.bar_ids.length} selected
                  </label>
                  <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto p-2 bg-slate-800/50 rounded-lg">
                    {bars.map((bar, idx) => (
                      <div
                        key={bar.id}
                        onClick={() => toggleBarSelection(bar.id)}
                        className={`p-2 rounded cursor-pointer transition-all ${
                          newCrawl.bar_ids.includes(bar.id)
                            ? 'bg-purple-600/30 border border-purple-500'
                            : 'bg-slate-700/50 border border-slate-600 hover:border-purple-500/50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {newCrawl.bar_ids.includes(bar.id) && (
                            <span className="w-5 h-5 rounded-full bg-purple-500 text-white text-xs flex items-center justify-center">
                              {newCrawl.bar_ids.indexOf(bar.id) + 1}
                            </span>
                          )}
                          <span className="text-sm text-white truncate">{bar.name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newCrawl.is_public}
                    onChange={(e) => setNewCrawl({ ...newCrawl, is_public: e.target.checked })}
                    className="rounded"
                  />
                  <label className="text-sm text-slate-300">Make this crawl public (anyone can join)</label>
                </div>

                <Button
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600"
                  onClick={handleCreateCrawl}
                  disabled={!newCrawl.name || newCrawl.bar_ids.length < 2}
                >
                  Create Bar Crawl
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Crawls List */}
          <div className="lg:col-span-1 space-y-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Route className="w-5 h-5 text-purple-400" />
              Your Crawls
            </h2>
            
            {crawls.length === 0 ? (
              <Card className="glass p-6 text-center">
                <Route className="w-12 h-12 mx-auto mb-3 text-slate-500" />
                <p className="text-slate-400">No bar crawls yet</p>
                <p className="text-sm text-slate-500 mt-1">Create one to get started!</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {crawls.map((crawl) => (
                  <Card
                    key={crawl.id}
                    className={`glass p-4 cursor-pointer transition-all ${
                      selectedCrawl?.id === crawl.id
                        ? 'border-purple-500'
                        : 'border-slate-700 hover:border-purple-500/50'
                    }`}
                    onClick={() => setSelectedCrawl(crawl)}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-white">{crawl.name}</h3>
                        <div className="flex items-center gap-2 text-xs text-slate-400 mt-1">
                          <Calendar className="w-3 h-3" />
                          <span>{crawl.date}</span>
                          <Clock className="w-3 h-3 ml-2" />
                          <span>{crawl.start_time}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-400 mt-1">
                          <MapPin className="w-3 h-3" />
                          <span>{crawl.route?.length || 0} stops</span>
                          <Users className="w-3 h-3 ml-2" />
                          <span>{crawl.participants?.length || 0} going</span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-500" />
                    </div>
                    
                    {!isParticipant(crawl) && crawl.is_public && (
                      <Button
                        size="sm"
                        className="mt-3 w-full bg-purple-600 hover:bg-purple-700"
                        onClick={(e) => { e.stopPropagation(); handleJoinCrawl(crawl.id); }}
                      >
                        Join Crawl
                      </Button>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Crawl Details & Map */}
          <div className="lg:col-span-2">
            {selectedCrawl ? (
              <Card className="glass p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-white">{selectedCrawl.name}</h2>
                    <p className="text-slate-400 text-sm">{selectedCrawl.description}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="border-slate-600" onClick={() => {
                      const url = `${window.location.origin}/bar-crawl?crawl=${selectedCrawl.id}`;
                      if (navigator.share) {
                        navigator.share({ title: `Join "${selectedCrawl.name}" Bar Crawl!`, text: `Come join our bar crawl on NightBuZz!`, url }).catch(() => {});
                      } else {
                        navigator.clipboard.writeText(url);
                        toast.success('Invite link copied!');
                      }
                    }}>
                      <Share2 className="w-4 h-4 mr-1" /> Invite Friends
                    </Button>
                    {selectedCrawl.creator_id === user?.id && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteCrawl(selectedCrawl.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-purple-400">{selectedCrawl.route?.length || 0}</p>
                    <p className="text-xs text-slate-400">Stops</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-pink-400">{selectedCrawl.total_distance_miles || 0}</p>
                    <p className="text-xs text-slate-400">Miles</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-amber-400">{selectedCrawl.estimated_duration_hours || 0}</p>
                    <p className="text-xs text-slate-400">Hours Est.</p>
                  </div>
                </div>

                {/* Map */}
                <div className="h-[300px] rounded-lg overflow-hidden mb-4">
                  {selectedCrawl.route?.length > 0 && (
                    <MapContainer
                      center={[
                        selectedCrawl.route[0]?.latitude || 33.4936,
                        selectedCrawl.route[0]?.longitude || -117.1484
                      ]}
                      zoom={13}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; OpenStreetMap'
                      />
                      
                      {/* Route line */}
                      <Polyline
                        positions={selectedCrawl.route
                          .filter(stop => stop.latitude && stop.longitude)
                          .map(stop => [stop.latitude, stop.longitude])}
                        color="#9333EA"
                        weight={4}
                        opacity={0.7}
                        dashArray="10, 10"
                      />
                      
                      {/* Stop markers */}
                      {selectedCrawl.route.map((stop, idx) => (
                        stop.latitude && stop.longitude && (
                          <Marker
                            key={stop.bar_id}
                            position={[stop.latitude, stop.longitude]}
                            icon={stopIcon(stop.order)}
                          >
                            <Popup>
                              <div className="p-2">
                                <p className="font-bold">{stop.order}. {stop.bar_name}</p>
                                <p className="text-xs text-gray-500">{stop.bar_address}</p>
                                {idx > 0 && (
                                  <p className="text-xs mt-1">
                                    <Navigation className="w-3 h-3 inline mr-1" />
                                    {stop.distance_from_prev_miles} mi from previous
                                  </p>
                                )}
                              </div>
                            </Popup>
                          </Marker>
                        )
                      ))}
                    </MapContainer>
                  )}
                </div>

                {/* Route List with Voting */}
                <div>
                  <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Navigation className="w-4 h-4 text-purple-400" />
                    Route ({selectedCrawl.route?.length} stops)
                  </h3>
                  <div className="space-y-2">
                    {selectedCrawl.route?.map((stop, idx) => (
                      <div
                        key={stop.bar_id}
                        className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg"
                      >
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                          {stop.order}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-white">{stop.bar_name}</p>
                          <p className="text-xs text-slate-400">
                            {idx > 0 && `${stop.distance_from_prev_miles} mi • `}
                            {stop.bar_address}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-green-400 hover:bg-green-500/20"
                            onClick={() => handleVote(selectedCrawl.id, stop.bar_id, 1)}
                          >
                            <ThumbsUp className="w-4 h-4" />
                          </Button>
                          <span className="text-sm font-medium text-white min-w-[20px] text-center">
                            {stop.votes || 0}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:bg-red-500/20"
                            onClick={() => handleVote(selectedCrawl.id, stop.bar_id, -1)}
                          >
                            <ThumbsDown className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Participants */}
                <div className="mt-4">
                  <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4 text-purple-400" />
                    Going ({selectedCrawl.participants?.length || 0})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedCrawl.participants?.map((p) => (
                      <div
                        key={p.user_id}
                        className="px-3 py-1 bg-purple-500/20 rounded-full text-sm text-purple-300"
                      >
                        {p.username}
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            ) : (
              <Card className="glass p-12 text-center">
                <Route className="w-16 h-16 mx-auto mb-4 text-slate-500" />
                <h3 className="text-xl font-semibold text-white mb-2">Select a Bar Crawl</h3>
                <p className="text-slate-400">
                  Choose a crawl from the list or create a new one to see the route map
                </p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarCrawl;
